import {
  Carer,
  NoticeItemResponse,
  NoticeListItemResponse,
} from '@lmc/ts-common'
import { Avatar, EmptyState, NotStartedIcon, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { noticeboardConstants } from 'locale/noticeboard'
import { Dispatch, Key, SetStateAction } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { formatDateToDay } from 'common/utils'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { UserDtos } from '@lmc/ts-common/lib/dtos/User'
import { isMobile } from 'react-device-detect'
import { Table, TableProps } from 'antd'
import { useSelector } from 'react-redux'
import { SorterResult } from 'antd/es/table/interface'

export const noticeboardDefaultSort = {
  seen: 1,
  updatedOn: -1,
  createdOn: -1,
} as const

export type NoticeboardSortObject = Partial<
  Record<keyof Omit<NoticeListItemResponse, 'seenBy'> | 'seen', 1 | -1>
>

type NoticesTableProps = {
  notices: NoticeListItemResponse[]
  page: number
  setPage: Dispatch<SetStateAction<number>>
  count: number
  isLoading: boolean
  onSortChange: (sort: NoticeboardSortObject) => void
}

type DataSource = {
  noticeId: string
  titleString: string
  title: NoticeListItemResponse
  body: string
  updatedBy: Pick<UserDtos, 'name' | 'id' | 'picture'>
  updatedOn: string
  seenBy: string[]
  view: string
}

const NoticesTable = ({
  notices,
  page,
  setPage,
  count,
  isLoading,
  onSortChange,
}: NoticesTableProps) => {
  const { t } = useTranslation(NAMESPACE.NOTICEBOARD)

  const navigate = useNavigate()

  const handleRowClick = (record: DataSource) => {
    navigate(`/noticeboard/${record.noticeId}`)
  }

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const columns = [
    {
      dataIndex: 'title',
      title: <Text weight="medium">{t(noticeboardConstants.TABLE_TITLE)}</Text>,
      key: 'title',
      sorter: true,
      render: (notice: NoticeItemResponse) => {
        const myID = user.id
        const isSeenByMe = notice.seenBy.map((user) => user.id).includes(myID)

        if (isSeenByMe) {
          return <Text>{notice.title}</Text>
        }

        return (
          <StyledNoticeTitle>
            <Text weight="bold">{notice.title}</Text>
            <Tag color="magenta">{t(noticeboardConstants.UNREAD)}</Tag>
          </StyledNoticeTitle>
        )
      },
    },
    {
      dataIndex: 'updatedBy',
      title: (
        <DataContainer>
          <Text weight="medium">{t(noticeboardConstants.LAST_UPDATED_BY)}</Text>
        </DataContainer>
      ),
      key: 'updatedBy',
      sorter: true,
      width: 200,

      render: (updatedBy: DataSource['updatedBy']) => (
        <PersonCard>
          <Avatar url={updatedBy?.picture} />
          <Text>{updatedBy?.name}</Text>
        </PersonCard>
      ),
    },
    {
      dataIndex: 'updatedOn',
      title: (
        <DataContainer>
          <NoBreakText weight="medium">
            {t(noticeboardConstants.LAST_UPDATED_ON)}
          </NoBreakText>
        </DataContainer>
      ),
      key: 'updatedOn',
      sorter: true,
      width: 120,

      render: (updatedOn: string) => (
        <DateContainer>
          <Text>{formatDateToDay(updatedOn)}</Text>
          <Text>{dayjs(updatedOn).format('HH:mm')}</Text>
        </DateContainer>
      ),
    },

    {
      dataIndex: 'view',
      title: ' ',
      key: 'view',
      width: 56,
      render: (noticeId: string) => (
        <Link to={noticeId}>
          <Text weight="medium">{t(noticeboardConstants.VIEW)}</Text>
        </Link>
      ),
    },
  ]

  const mobileColumns = columns.filter((column) => column.key !== 'updatedBy')

  const dataSource: DataSource[] = notices?.map(
    (notice: NoticeListItemResponse): DataSource => ({
      noticeId: notice.id,
      titleString: notice.title,
      title: notice,
      body: notice.body,
      updatedBy: notice.updatedBy || notice.createdBy,
      updatedOn: notice.updatedOn?.toString() || notice.createdOn.toString(),
      seenBy: notice.seenBy.map((user) => user?.picture),
      view: notice.id,
    })
  )

  const handleTableChange: TableProps<DataSource>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const sortObject: NoticeboardSortObject = {}

    const applySortOrder = (s: SorterResult<DataSource>) =>
      s.order ? { [s.columnKey as Key]: getSortNumber(s.order) } : {}

    if (Array.isArray(sorter)) {
      sorter.forEach((i) => Object.assign(sortObject, applySortOrder(i)))
    } else {
      Object.assign(sortObject, applySortOrder(sorter))
    }

    onSortChange(sortObject)
  }

  if (!notices.length && !isLoading) {
    return (
      <EmptyState
        icon={<NotStartedIcon />}
        title={t(noticeboardConstants.NO_NOTICES_YET)}
        description={t(noticeboardConstants.NO_NOTICES_YET_DESCRIPTION)}
      />
    )
  }

  return (
    <TableContainer>
      <Table<DataSource>
        onChange={handleTableChange}
        loading={isLoading}
        dataSource={dataSource}
        columns={isMobile ? mobileColumns : columns}
        rowKey="noticeId"
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
        })}
        components={{
          body: {
            row: ClickableTableRow,
          },
        }}
        pagination={{
          pageSize: 10,
          current: page,
          onChange: (page) => {
            setPage(page)
          },
          total: count,
          showSizeChanger: false,
          position: ['bottomCenter'],
        }}
      />
    </TableContainer>
  )
}

const getSortNumber = (sortOrder: 'ascend' | 'descend'): 1 | -1 =>
  sortOrder === 'ascend' ? 1 : -1

const TableContainer = styled.div`
  margin-top: 24px;
  .ant-table-column-sorters {
    gap: 8px;
    flex-direction: row-reverse;
  }
`

const ClickableTableRow = styled.tr`
  cursor: pointer;
`

const StyledNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NoBreakText = styled(Text)`
  white-space: nowrap;
`

const DataContainer = styled.div``

const DateContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`

const PersonCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

export default NoticesTable
